import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, MaskedLine, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink } from "../components/Buttons";
import { PROJECTS, IMAGES } from "../data/site";
import { VideoPlayer } from "../components/VideoPlayer";

const PILLARS = [
  {
    word: "Story",
    num: "01",
    copy: "We record the voices — letters, vows, toasts, grandparents — and build the film around them. The day provides the plot; the people provide the meaning.",
  },
  {
    word: "Scene",
    num: "02",
    copy: "Long lenses, small footprint, no choreography. We frame the day as it actually unfolds, with light that flatters and moments that weren't staged.",
  },
  {
    word: "Sound",
    num: "03",
    copy: "Pristine audio hidden in flowers and lapels, plus a score that fits your story — because fifty years from now, the voices are what matter.",
  },
];

const FILMS = PROJECTS.filter((p) => p.tags.includes("S3 Films"));

export default function S3Films() {
  useDocTitle("S3 Films");
  return (
    <div data-testid="s3-films-page">
      <PageHero
        eyebrow="S3 Films · The cinematic division of Studio Solutions"
        lines={[<>Every story</>, <span key="g" className="text-gold-dark">deserves cinema.</span>]}
        sub="S3 Films is the entire cinematic and video side of Studio Solutions — weddings, music videos, documentaries, promos, and story-driven content, all held to one standard: Story. Scene. Sound."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="films-hero-cta">Check Your Date</GoldLink>
        </div>
      </PageHero>

      {/* STORY. SCENE. SOUND. */}
      <section className="border-y border-ink/10 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">The S3 Framework</p>
          </Reveal>
          <h2 className="mt-6 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.1}>Story.</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="text-gold-dark">Scene.</span>
            </MaskedLine>
            <MaskedLine delay={0.34}>Sound.</MaskedLine>
          </h2>
          <Reveal>
            <p className="mt-6 max-w-2xl font-serif text-xl italic text-smoke">
              Three words that named this division — and grew into the philosophy behind all of
              Studio Solutions.
            </p>
          </Reveal>
          <div className="mt-16 grid gap-px bg-ink/10 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.word} delay={i * 0.1} className="bg-cream">
                <div className="h-full p-8 sm:p-10">
                  <p className="font-mono text-xs tracking-[0.3em] text-gold-dark">{p.num}</p>
                  <h3 className="mt-5 font-serif text-3xl italic text-ink">{p.word}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-smoke">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FILMS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="Recent Films"
          title={<>Made to be <span className="text-gold-dark">rewatched.</span></>}
          sub="Real films for real clients — weddings, music videos, promos and documentaries. Press play."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {FILMS.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.08}>
              <div className="group overflow-hidden border border-ink/10 bg-white shadow-lg">
                {f.video ? (
                  <VideoPlayer
                    src={f.video}
                    poster={f.poster}
                    className="h-80 w-full bg-night object-cover sm:h-[420px]"
                    testId={`film-video-${f.id}`}
                  />
                ) : (
                  <div className="relative overflow-hidden">
                    <MediaImg
                      src={f.image}
                      alt={f.title}
                      className="h-80 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-[420px]"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-4 p-5">
                  <h3 className="font-serif text-2xl italic text-ink">{f.title}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">{f.location}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-8 overflow-hidden border border-ink/10">
            <MediaImg src={IMAGES.saraCalvinBW} alt="Sara & Calvin wedding portrait" className="h-64 w-full object-cover sm:h-96" />
          </div>
        </Reveal>
      </section>

      {/* APPROACH */}
      <section className="bg-night text-sand">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-2 lg:px-16">
          <div>
            <SectionHeading
              dark
              eyebrow="How We Work"
              title={<>Present, <span className="text-gold">not production.</span></>}
              sub="The best compliment we get: 'We barely noticed you — and you caught everything.'"
            />
            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-5">
                {[
                  "A pre-production call to learn the story, not just the schedule",
                  "Small footprint — real moments over staged ones",
                  "Audio captured like it matters, because it does",
                  "Teasers fast; feature films within the season",
                ].map((t) => (
                  <li key={t} className="flex gap-4 text-sm leading-relaxed text-sand/75">
                    <span className="mt-1.5 block h-2 w-2 shrink-0 bg-gold" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <Link
                  to="/start-a-project"
                  data-testid="films-approach-cta"
                  className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.18em] text-gold transition-colors duration-300 hover:text-sand"
                >
                  Tell us your date
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="clip-frame overflow-hidden shadow-2xl">
              <MediaImg src={IMAGES.saraCalvinFront} alt="Sara & Calvin wedding portrait" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Let's make your story cinematic."
        sub="Weddings, music videos, documentaries, promos — tell us the story and S3 Films brings the cinema."
      />
    </div>
  );
}
